import React, { createContext, useContext, useState, useEffect } from 'react';
import { vipAPI } from '../services/api';
import { useAuth } from './AuthContext';
import { useBrand } from './BrandContext';

const AgentContext = createContext(null);

const STORAGE_KEY = 'selected_vip_agent';

export function AgentProvider({ children }) {
  const { user, hasPermission } = useAuth();
  // Only VIP dashboard users pick agents; anyone else (the private VIP dashboard among them) would
  // get a 403 from /api/vip/agents.
  const canPickAgent = Boolean(user) && hasPermission('view:vip_dashboard');
  const { activeBrand } = useBrand();
  const [agents, setAgents] = useState([]);
  const [agentsError, setAgentsError] = useState('');
  const [selectedAgent, setSelectedAgentState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || ''; } catch { return ''; }
  });

  // Re-fetch whenever the user or the active brand changes. Agents are per brand — the request
  // carries the new X-Brand — and the selected agent resets to the new brand's first agent when
  // the stored one does not exist there. A change also cancels the previous request: its late
  // response must not install the old brand's agents or persist one of them as selected.
  useEffect(() => {
    if (!canPickAgent) return;
    setAgentsError('');
    let cancelled = false;
    vipAPI.getAgents()
      .then(res => {
        if (cancelled) return;
        const list = res.data.agents || [];
        setAgents(list);
        // Auto-select the first agent if nothing is stored yet
        setSelectedAgentState(prev => {
          if (prev && list.includes(prev)) return prev;
          const first = list[0] || '';
          try { localStorage.setItem(STORAGE_KEY, first); } catch {}
          return first;
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setAgentsError(err?.response?.data?.error || err?.message || 'Failed to load agents');
      });
    return () => { cancelled = true; };
  }, [user, activeBrand, canPickAgent]);

  const setSelectedAgent = (agent) => {
    try { localStorage.setItem(STORAGE_KEY, agent); } catch {}
    setSelectedAgentState(agent);
  };

  return (
    <AgentContext.Provider value={{ agents, agentsError, selectedAgent, setSelectedAgent }}>
      {children}
    </AgentContext.Provider>
  );
}

export function useAgent() {
  const ctx = useContext(AgentContext);
  if (!ctx) throw new Error('useAgent must be used within AgentProvider');
  return ctx;
}
